/**
 * OAuth Handlers for Media Platforms
 *
 * ONLY `generateOAuthState` is on a live path — ConnectPlatformModal calls it
 * when it builds an authorize URL. Everything else here (getOAuthUrl,
 * openOAuthPopup, listenForOAuthCallback, exchangeCodeForToken,
 * platformOAuthConfig, parseOAuthState) has no caller in src/, api/ or
 * scripts/, and has drifted from the flow the app actually runs.
 *
 * THE LIVE OAUTH PATH IS NOT IN THIS FILE. It is:
 *   1. authorize  — ConnectPlatformModal builds the URL per platform
 *   2. callback   — OAuthCallback reads the code off the redirect
 *   3. exchange   — POST /api/oauth/exchange -> api/_lib/oauthExchange.ts,
 *                   server-side because it needs the app secret
 *
 * Kept rather than deleted, deliberately. Read it as reference, not as
 * something to wire up: getOAuthUrl's TikTok branch in particular disagrees
 * with the live path on all three of endpoint, parameters and callback
 * parameter name (see the note on getTikTokOAuthUrl), so adopting it would
 * break TikTok connect rather than enable it.
 */

import type { PlatformType, OAuthState, OAuthCallbackParams } from "@/types";

/**
 * Generate OAuth state parameter for security
 */
export function generateOAuthState(
  platform: PlatformType,
  _workspaceId: string
): string {
  const state: OAuthState = {
    state: crypto.randomUUID?.() || Math.random().toString(36).substring(7),
    platform,
    redirectUri: `${window.location.origin}/settings/integrations/callback`,
    timestamp: Date.now(),
  };

  return btoa(JSON.stringify(state));
}

/**
 * Parse and validate OAuth state parameter
 */
export function parseOAuthState(
  state: string
): OAuthState | null {
  try {
    const parsed = JSON.parse(atob(state)) as OAuthState;

    // Validate timestamp (state expires after 10 minutes)
    if (Date.now() - parsed.timestamp > 10 * 60 * 1000) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

/**
 * Get OAuth URL for a platform.
 *
 * NO CALLERS. ConnectPlatformModal builds these URLs itself; this is the
 * earlier implementation it replaced, kept for reference. Its per-platform
 * branches have not been maintained alongside the live ones — verify against
 * ConnectPlatformModal before trusting any of them.
 */
export function getOAuthUrl(
  platform: PlatformType,
  state: string,
  _workspaceId?: string
): string {
  const redirectUri = `${window.location.origin}/settings/integrations/callback`;

  switch (platform) {
    case "facebook":
      return getFacebookOAuthUrl(state, redirectUri);

    case "google_ads":
    case "google_analytics":
      return getGoogleOAuthUrl(platform, state, redirectUri);

    case "tiktok":
      return getTikTokOAuthUrl(state, redirectUri);

    case "line":
      throw new Error("LINE does not support OAuth. Please use API key authentication.");

    default:
      throw new Error(`Unsupported platform: ${platform}`);
  }
}

/**
 * Facebook OAuth URL
 */
function getFacebookOAuthUrl(state: string, redirectUri: string): string {
  const appId = import.meta.env.VITE_FACEBOOK_APP_ID;

  if (!appId) {
    throw new Error("Facebook App ID not configured");
  }

  const params = new URLSearchParams({
    client_id: appId,
    redirect_uri: redirectUri,
    scope: "ads_read,ads_management,instagram_basic,instagram_manage_insights",
    response_type: "code",
    state,
  });

  return `https://www.facebook.com/v18.0/dialog/oauth?${params.toString()}`;
}

/**
 * Google OAuth URL
 */
function getGoogleOAuthUrl(
  platform: "google_ads" | "google_analytics",
  state: string,
  redirectUri: string
): string {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  if (!clientId) {
    throw new Error("Google Client ID not configured");
  }

  // Scopes based on platform type
  const scopes =
    platform === "google_ads"
      ? [
          "https://www.googleapis.com/auth/adwords",
          "https://www.googleapis.com/auth/spreadsheets.readonly",
        ]
      : [
          "https://www.googleapis.com/auth/analytics.readonly",
          "https://www.googleapis.com/auth/analytics",
        ];

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: scopes.join(" "),
    response_type: "code",
    state,
    access_type: "offline",
    prompt: "consent",
  });

  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

/**
 * TikTok OAuth URL — SUPERSEDED, and wrong on every point that matters.
 *
 * Against the live path in ConnectPlatformModal:
 *   endpoint   ads.tiktok.com/oauth2/v1/authorize   vs business-api.tiktok.com/portal/auth
 *   parameters sends scope + response_type          vs app_id/redirect_uri/state only
 *              (TikTok configures scopes on the app, not in the URL)
 *   callback   assumes `code`                       vs `auth_code`
 *
 * The token exchange that pairs with the live URL lives in
 * api/_lib/oauthExchange.ts and posts auth_code to
 * business-api.tiktok.com/open_api/v1.3/oauth2/access_token/.
 */
function getTikTokOAuthUrl(state: string, redirectUri: string): string {
  const appId = import.meta.env.VITE_TIKTOK_APP_ID;

  if (!appId) {
    throw new Error("TikTok App ID not configured");
  }

  const params = new URLSearchParams({
    app_id: appId,
    redirect_uri: redirectUri,
    scope: "advertiser_info",
    response_type: "code",
    state,
  });

  return `https://ads.tiktok.com/oauth2/v1/authorize?${params.toString()}`;
}

/**
 * Open OAuth popup window
 */
export function openOAuthPopup(
  url: string,
  _title = "Connect Account"
): Window | null {
  const width = 600;
  const height = 700;
  const left = window.innerWidth / 2 - width / 2;
  const top = window.innerHeight / 2 - height / 2;

  return window.open(
    url,
    "oauth-popup",
    `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
  );
}

/**
 * Listen for OAuth callback from popup
 */
export function listenForOAuthCallback(
  state: string,
  timeout = 5 * 60 * 1000 // 5 minutes
): Promise<{ code: string; state: string }> {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error("OAuth flow timed out"));
    }, timeout);

    const messageHandler = (event: MessageEvent) => {
      // Verify origin
      if (event.origin !== window.location.origin) return;

      const { code, state: returnedState, error } = event.data as OAuthCallbackParams;

      if (error) {
        cleanup();
        reject(new Error(error));
        return;
      }

      if (code && returnedState === state) {
        cleanup();
        resolve({ code, state: returnedState });
      }
    };

    const cleanup = () => {
      clearTimeout(timeoutId);
      window.removeEventListener("message", messageHandler);
    };

    window.addEventListener("message", messageHandler);
  });
}

/**
 * Exchange OAuth code for access token.
 *
 * SECURITY: Token exchange ALWAYS requires a client_secret / app_secret. Doing
 * it from the browser inlines that secret into the bundle and exposes the
 * resulting access/refresh tokens to whoever opens DevTools. This function is
 * intentionally stubbed and must be replaced by a server-side proxy (Vercel
 * serverless function under api/oauth/exchange.ts) before any platform
 * integration ships.
 *
 * The OAuth *authorization URL* (above) is fine to build client-side because
 * it only contains the public client_id. Only the **token exchange** must
 * move server-side.
 */
export async function exchangeCodeForToken(
  _platform: PlatformType,
  _code: string,
  _redirectUri: string
): Promise<{
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  account_id?: string;
}> {
  throw new Error(
    "OAuth token exchange is not implemented in the browser. " +
      "Add a server-side proxy (api/oauth/exchange) and call it from here. " +
      "See api/gemini-chat.ts for the pattern."
  );
}

/**
 * Platform configuration for OAuth
 */
export const platformOAuthConfig: Record<
  PlatformType,
  {
    supportsOAuth: boolean;
    requiresBackend: boolean;
    documentationUrl: string;
  }
> = {
  facebook: {
    supportsOAuth: true,
    requiresBackend: true,
    documentationUrl: "https://developers.facebook.com/docs/facebook-login/",
  },
  google_ads: {
    supportsOAuth: true,
    requiresBackend: true,
    documentationUrl: "https://developers.google.com/google-ads/api/docs/oauth",
  },
  tiktok: {
    supportsOAuth: true,
    requiresBackend: true,
    documentationUrl: "https://ads.tiktok.com/marketing_api/docs?id=1738587022722049",
  },
  line: {
    supportsOAuth: false,
    requiresBackend: false,
    documentationUrl: "https://developers.line.biz/en/docs/messaging-api/",
  },
  google_analytics: {
    supportsOAuth: true,
    requiresBackend: true,
    documentationUrl: "https://developers.google.com/analytics/devguides/reporting/data/v1",
  },
};
